// Prefer configured API base, otherwise use relative fetch so it hits the same domain/worker.
const baseUrl = process.env.NEXT_PUBLIC_API_BASE || '';

export const resolveApiUrl = (path: string) => {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (!baseUrl) {
    return path;
  }
  return new URL(path, baseUrl).toString();
};

export async function fetchDynamicData<T>(path: string, fallback: T): Promise<T> {
  try {
    const url = resolveApiUrl(path);
    const res = await fetch(url, { cache: 'no-store' });

    if (!res.ok) {
      return fallback;
    }

    const json = (await res.json()) as T;
    return json ?? fallback;
  } catch (error) {
    console.warn(`Falling back for ${path} due to fetch error`, error);
    return fallback;
  }
}

export type FetchDebugStatus = {
  ok: boolean;
  status?: number;
  durationMs: number;
  error?: string;
};

export async function fetchDynamicDataWithMeta<T>(
  path: string,
  fallback: T
): Promise<{ data: T; debug: FetchDebugStatus }> {
  const start = Date.now();
  try {
    const url = resolveApiUrl(path);
    const res = await fetch(url, { cache: 'no-store' });
    const durationMs = Date.now() - start;

    if (!res.ok) {
      return {
        data: fallback,
        debug: { ok: false, status: res.status, durationMs },
      };
    }

    const json = (await res.json()) as T;
    return {
      data: (json ?? fallback) as T,
      debug: { ok: true, status: res.status, durationMs },
    };
  } catch (error) {
    const durationMs = Date.now() - start;
    return {
      data: fallback,
      debug: {
        ok: false,
        durationMs,
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }
}
